import { vuesDeFicheDeLaPorte } from '@taki/data-model';
import type { AppScreen } from '@taki/data-model';
import { cheminDeStockage, fichesDuParent, valeurAuChemin, vuesDe } from '@taki/ui';
import type { ChampDeclare, LinkedDoor, PorteAssemblee, ViewId, ViewType } from '@taki/ui';
import type { FicheGenerique } from './app-declaree.js';

/**
 * CE QUE LES VUES DE L'APP PARTAGENT SANS REACT (chantier `fiche-suivi`) : la
 * clef d'une vue Canvas dans le manifeste, et les donnees qu'une vue HTML
 * isolee recoit. Pur, pour que le montage (`definirAppDeclaree`) et les tests
 * le lisent sans tirer le design system. Le rendu vit dans `vues-de-fiche.tsx`.
 */

/** La clef d'une vue Canvas dans le catalogue `canvas` du manifeste. */
export const PREFIXE_SURFACE_DE_VUE = 'vue:';
export const clefDeSurfaceDeVue = (vueId: string): string => `${PREFIXE_SURFACE_DE_VUE}${vueId}`;

/** Les fiches liees qu'une vue HTML recoit au plus, par lien (archives exclues). */
export const FICHES_LIEES_MAX = 200;

/* ------------------------------------------------------------------ */
/* Les donnees d'une vue HTML : fonction pure                          */
/* ------------------------------------------------------------------ */

/** Ce que le cadre d'une vue HTML recoit (protocole v1, `docs/socle/vues-html.md`). */
export interface DonneesDeVueHtml {
  fiche: {
    id: string;
    family: string;
    type?: string;
    champs: Record<string, unknown>;
    libelles: Record<string, string>;
  };
  liees: Record<string, { id: string; titre: string }[]>;
}

/**
 * UNE VALEUR EN JSON SUR : un instant (Date, Timestamp) en ISO 8601, une date
 * pure telle quelle (`YYYY-MM-DD`), une liste valeur par valeur, le vide en
 * `null`. Un objet passe par une copie JSON : le cadre recoit des donnees, ni
 * une classe ni une reference vers la memoire de l'ecran.
 */
export function valeurPourLeCadre(valeur: unknown): unknown {
  if (valeur === undefined || valeur === null || valeur === '') return null;
  if (valeur instanceof Date) return Number.isNaN(valeur.getTime()) ? null : valeur.toISOString();
  if (typeof valeur === 'object' && typeof (valeur as { toDate?: unknown }).toDate === 'function') {
    return valeurPourLeCadre((valeur as { toDate(): Date }).toDate());
  }
  if (Array.isArray(valeur)) return valeur.map((element) => valeurPourLeCadre(element));
  if (typeof valeur === 'object') {
    try {
      return JSON.parse(JSON.stringify(valeur)) as unknown;
    } catch {
      return null;
    }
  }
  return valeur;
}

export interface EntreeDesDonnees {
  fiche: FicheGenerique;
  jeu: { family: string; type?: string | undefined };
  vue: { champs: readonly string[]; liens?: readonly string[] | undefined };
  /** Les champs declares du jeu : ils disent si un nom vit dans `customFields`. */
  champsDuJeu: readonly ChampDeclare[];
  /** Les catalogues connus, par chemin de stockage : valeur -> libelle. */
  options: ReadonlyMap<string, ReadonlyMap<string, string>>;
  /** Les liens de la porte. */
  liens: readonly LinkedDoor[];
  /** Le jeu lie charge en entier, ou `undefined`. */
  lireJeu(lien: LinkedDoor): readonly FicheGenerique[] | undefined;
  /** Le titre d'une fiche liee, tel que sa porte l'affiche. */
  titreDe(lien: LinkedDoor, fiche: FicheGenerique): string;
  /**
   * CE QUE LA FICHE AFFICHE d'un champ qu'elle ne stocke pas tel quel (extension
   * additive du protocole v1, chantier `fiche-canva-v2`) : le NOM de la fiche
   * designee par un champ de reference, la valeur d'un champ de lecture. Clef =
   * le nom declare par la vue. Absent : les donnees d'avant, a l'identique.
   * `affichesDeLaFiche` le fabrique de la lecture de la Fiche.
   */
  affiches?: ReadonlyMap<string, string> | undefined;
}

/** Ce nom designe-t-il un champ de LECTURE du jeu (sans valeur stockee) ? */
function estUneLecture(nom: string, champsDuJeu: readonly ChampDeclare[]): boolean {
  return champsDuJeu.some((champ) => champ.key === nom && champ.type === 'lecture');
}

/** Ce que la lecture de la Fiche sait d'un champ : de quoi decider s'il s'affiche autrement que stocke. */
export interface ChampLuDeLaFiche {
  valeur: unknown;
  texte: string | null;
  reference?: unknown;
}

/**
 * CE QUE LA FICHE OUVERTE AFFICHE, pour les champs qu'une vue HTML declare et
 * qu'une valeur stockee ne dit pas : un champ de LECTURE (lu sur une fiche
 * liee, il n'a aucune valeur stockee) et un champ de REFERENCE (la fiche
 * stocke un id, elle affiche le nom de la fiche designee). Le texte est celui
 * de la cellule projetee par la Fiche (`LectureDeLaFiche`), jamais une relecture
 * de la base. Un champ vide, ou une reference dont le nom n'est pas encore
 * charge (la cellule redirait l'id nu), n'y figure pas : la vue lit alors
 * `champs[nom]` comme avant. Plusieurs fiches designees : leurs noms, joints
 * par une virgule.
 */
export function affichesDeLaFiche(
  lecture: { champ(nom: string): ChampLuDeLaFiche | undefined } | null,
  noms: readonly string[],
  champsDuJeu: readonly ChampDeclare[],
): ReadonlyMap<string, string> {
  const affiches = new Map<string, string>();
  if (lecture === null) return affiches;
  for (const nom of noms) {
    const champ = lecture.champ(nom);
    if (champ === undefined || champ.texte === null) continue;
    if (estUneLecture(nom, champsDuJeu)) {
      affiches.set(nom, champ.texte);
      continue;
    }
    if (champ.reference === undefined) continue;
    const valeurs = Array.isArray(champ.valeur) ? champ.valeur.map(String) : [String(champ.valeur ?? '')];
    const texte = champ.texte.split('\n').join(', ');
    if (valeurs.join(', ') === texte) continue;
    affiches.set(nom, texte);
  }
  return affiches;
}

/**
 * LES DONNEES D'UNE VUE HTML POUR UNE FICHE : la fiche reduite aux champs que la
 * vue DECLARE (clef = le nom declare, `socle:name` ou `budget`), le libelle de
 * l'option pour un champ a catalogue, et pour chaque lien declare les fiches
 * liees (`{ id, titre }`, archives exclues, `FICHES_LIEES_MAX` au plus). Rien
 * d'autre ne sort : la vue ne voit que ce qu'elle a demande.
 */
export function donneesDeVueHtml(entree: EntreeDesDonnees): DonneesDeVueHtml {
  const { fiche, vue } = entree;
  const id = String(fiche['id'] ?? '');
  const champs: Record<string, unknown> = {};
  const libelles: Record<string, string> = {};
  for (const nom of vue.champs) {
    const chemin = cheminDeStockage(nom, entree.champsDuJeu);
    const brute = valeurAuChemin(fiche, chemin);
    champs[nom] = valeurPourLeCadre(brute);
    /* CE QUE LA FICHE AFFICHE, quand l'ecran le sait (`affiches`) : une lecture
       n'a pas de valeur stockee, le texte devient aussi sa valeur ; une
       reference garde son id, et le nom de la fiche designee va dans `libelles`. */
    const affiche = entree.affiches?.get(nom);
    if (affiche !== undefined) {
      libelles[nom] = affiche;
      if (estUneLecture(nom, entree.champsDuJeu)) champs[nom] = affiche;
      continue;
    }
    const catalogue = entree.options.get(chemin);
    if (catalogue === undefined || brute == null || brute === '') continue;
    const valeurs = Array.isArray(brute) ? brute : [brute];
    const mots = valeurs.map((valeur) => catalogue.get(String(valeur)) ?? String(valeur));
    libelles[nom] = mots.join(', ');
  }
  const liees: Record<string, { id: string; titre: string }[]> = {};
  for (const lienId of vue.liens ?? []) {
    const lien = entree.liens.find((candidat) => candidat.id === lienId);
    if (lien === undefined) {
      liees[lienId] = [];
      continue;
    }
    /* LE MEME FILTRE QUE LA PORTE OUVERTE DANS UNE FICHE (`fichesDuParent`,
       `porteDans.ts`) : une seule lecture du chemin pour tout le socle. */
    liees[lienId] = fichesDuParent(entree.lireJeu(lien) ?? [], lien, id, fiche)
      .filter((liee) => liee['archivedAt'] == null)
      .slice(0, FICHES_LIEES_MAX)
      .map((liee) => ({ id: String(liee['id'] ?? ''), titre: entree.titreDe(lien, liee) }));
  }
  return {
    fiche: {
      id,
      family: entree.jeu.family,
      ...(entree.jeu.type === undefined ? {} : { type: entree.jeu.type }),
      champs,
      libelles,
    },
    liees,
  };
}


/* ------------------------------------------------------------------ */
/* Le catalogue de vues de chaque porte                                */
/* ------------------------------------------------------------------ */

/**
 * LE CATALOGUE DE VUES D'UN ECRAN ET DE CHACUNE DE SES PORTES : les vues du
 * socle et les vues Canvas de collection que `vues` monte, dans son ordre ;
 * puis les VUES DE FICHE de l'app (Canvas de niveau fiche, vues HTML), qui ne
 * s'ecrivent pas dans `vues` : elles s'offrent PAR JEU, restreintes par
 * `vuesFiche` (`vuesDeFicheDeLaPorte`, une seule regle). `core.fiche` reste
 * ainsi la premiere vue de fiche de chaque porte.
 *
 * SANS VUE DE FICHE DE L'APP, les portes sortent telles qu'elles sont entrees :
 * aucun ecran existant ne change de catalogue.
 */
export function rangerLesVues(
  ecran: Pick<AppScreen, 'vues' | 'portes' | 'vuesCustom' | 'vuesHtml'>,
  portes: readonly PorteAssemblee[],
  duSocle: readonly ViewType[],
  deLApp: readonly ViewType[],
): { types: ViewType[]; portes: PorteAssemblee[] } {
  const typesDeCollection = vuesDe(
    ecran.vues.filter((id) => !deLApp.some((type) => type.id === id && type.level === 'record')),
    [...duSocle, ...deLApp.filter((type) => type.level === 'collection')],
  );
  const vuesDeFiche = deLApp.filter((type) => type.level === 'record');
  const types = [...typesDeCollection, ...vuesDeFiche];
  if (vuesDeFiche.length === 0) return { types, portes: [...portes] };
  return {
    types,
    portes: portes.map((porte) => {
      /* Le catalogue de la porte s'ecrit en entier : sans lui, elle heriterait
         des vues de fiche des autres jeux. */
      const base = (porte.viewIds ?? typesDeCollection.map((type) => type.id))
        .filter((id) => typesDeCollection.some((type) => type.id === id));
      const siennes = vuesDeFicheDeLaPorte(ecran, porte.id)
        .filter((id) => vuesDeFiche.some((type) => type.id === id)) as ViewId[];
      return { ...porte, viewIds: [...base, ...siennes] };
    }),
  };
}
